import { createTheme, responsiveFontSizes, alpha } from '@mui/material/styles';
import { CONTENT_CHANNEL_DESKTOP, CONTENT_CHANNEL_MOBILE, resolveContentChannel } from '../shared/channel/contentChannel';

const R5_CHANNEL_TOKENS = {
    [CONTENT_CHANNEL_DESKTOP]: {
        radius: 14
    },
    [CONTENT_CHANNEL_MOBILE]: {
        radius: 14
    }
};

const activeContentChannel = resolveContentChannel();
const activeTokens = R5_CHANNEL_TOKENS[activeContentChannel] || R5_CHANNEL_TOKENS[CONTENT_CHANNEL_DESKTOP];
const R5_RADIUS = activeTokens.radius;

// R5 Enterprise Palette
const palette = {
    primary: {
        main: '#CC0000', // R5 Red
        dark: '#CC0000', // Keep strict brand red across states
        light: '#CC0000', // Keeping strict to brand red
        contrastText: '#FFFFFF',
    },
    secondary: {
        main: '#000000',
        light: '#333333',
        contrastText: '#FFFFFF',
    },
    background: {
        default: '#FFFFFF',
        paper: '#F8F9FA',
        dark: '#000000',
    },
    text: {
        primary: '#000000',
        secondary: 'rgba(0, 0, 0, 0.6)',
        light: '#FFFFFF',
    },
};

// Typography System - Antigravity V2 (High-Level Professional)
const typography = {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif', // Added Inter for sharpness
    h1: {
        fontWeight: 600, // Premium SemiBold (vs 700)
        letterSpacing: '-0.03em', // Cinematic Tightness
        lineHeight: 1.1,
        fontSize: '3.5rem',
        color: '#1a1a1a', // Off-Black
    },
    h2: {
        fontWeight: 600,
        letterSpacing: '-0.025em',
        lineHeight: 1.2,
        fontSize: '2.5rem',
        color: '#1a1a1a',
    },
    h3: {
        fontWeight: 600,
        lineHeight: 1.2,
        fontSize: '2rem',
        letterSpacing: '-0.025em',
        color: '#1a1a1a',
    },
    h4: {
        fontWeight: 600,
        lineHeight: 1.3,
        fontSize: '1.5rem',
        letterSpacing: '-0.02em',
        color: '#1a1a1a',
    },
    h5: {
        fontWeight: 600,
        lineHeight: 1.4,
        fontSize: '1.25rem',
        letterSpacing: '-0.02em',
        color: '#1a1a1a',
    },
    h6: {
        fontWeight: 600,
        lineHeight: 1.4,
        fontSize: '1rem',
        letterSpacing: '-0.01em',
        color: '#1a1a1a',
        textTransform: 'uppercase', // Technical Heading
    },
    subtitle1: {
        fontWeight: 600, // Lead Text
        fontSize: '1.125rem', // 18px
        lineHeight: 1.5,
        letterSpacing: '-0.015em',
        color: '#333333',
    },
    subtitle2: {
        fontWeight: 600, // Emphasized Body
        fontSize: '0.875rem', // 14px
        lineHeight: 1.57, // Higher leading for density
        letterSpacing: '-0.01em',
        color: '#333333',
    },
    body1: {
        fontWeight: 400,
        fontSize: '1rem', // 16px
        lineHeight: 1.6, // Optimal Readability
        letterSpacing: '-0.01em',
        color: '#444444',
    },
    body2: {
        fontWeight: 400,
        fontSize: '0.875rem', // 14px
        lineHeight: 1.6,
        letterSpacing: '-0.01em',
        color: '#555555',
    },
    button: {
        fontWeight: 600,
        fontSize: '0.9375rem', // 15px
        letterSpacing: '-0.01em',
        textTransform: 'none', // Strict No-Caps
        lineHeight: 1.2,
    },
    caption: {
        fontWeight: 500,
        fontSize: '0.75rem', // 12px
        lineHeight: 1.66,
        letterSpacing: '0em', // Neutral
        color: '#777777',
    },
    overline: {
        fontWeight: 700,
        fontSize: '0.75rem',
        lineHeight: 2.6,
        letterSpacing: '0.1em',
        textTransform: 'uppercase',
        color: '#999999',
    },
};

// Component Overrides
const components = {
    MuiPaper: {
        styleOverrides: {
            rounded: {
                borderRadius: R5_RADIUS,
            },
        },
    },
    MuiCard: {
        styleOverrides: {
            root: {
                borderRadius: R5_RADIUS,
            },
        },
    },
    MuiAccordion: {
        styleOverrides: {
            root: {
                borderRadius: R5_RADIUS,
                overflow: 'hidden',
                '&:before': {
                    display: 'none',
                },
            },
        },
    },
    MuiDialog: {
        styleOverrides: {
            paper: {
                borderRadius: R5_RADIUS,
            },
        },
    },
    MuiDrawer: {
        styleOverrides: {
            paper: {
                borderRadius: R5_RADIUS,
            },
        },
    },
    MuiButton: {
        defaultProps: {
            disableElevation: true,
        },
        styleOverrides: {
            root: {
                borderRadius: R5_RADIUS,
                fontWeight: 600,
                textTransform: 'none',
                letterSpacing: '-0.01em',
                padding: '12px 32px',
                transition: 'all 0.4s cubic-bezier(0.23, 1, 0.32, 1)',
                '&:hover': {
                    transform: 'translateY(-2px)',
                },
                '&:disabled': {
                    backgroundColor: '#f5f5f5',
                    color: '#aaa',
                }
            },
            containedPrimary: {
                backgroundColor: '#CC0000',
                color: '#FFFFFF',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
                '&:hover': {
                    backgroundColor: '#CC0000',
                    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)',
                }
            },
            outlinedPrimary: {
                borderColor: '#CC0000',
                color: '#CC0000',
                borderWidth: '1.5px',
                '&:hover': {
                    backgroundColor: 'rgba(204, 0, 0, 0.04)',
                    borderColor: '#CC0000',
                    borderWidth: '1.5px',
                }
            },
            outlinedSecondary: {
                borderColor: '#1a1a1a',
                color: '#1a1a1a',
                '&:hover': {
                    backgroundColor: 'rgba(26, 26, 26, 0.04)',
                    borderColor: '#1a1a1a',
                }
            }
        },
    },
    MuiTextField: {
        defaultProps: {
            variant: 'outlined',
            fullWidth: true,
            InputLabelProps: { shrink: true },
        },
    },
    MuiOutlinedInput: {
        styleOverrides: {
            root: {
                borderRadius: R5_RADIUS,
                backgroundColor: '#ffffff',
                transition: 'all 0.2s ease-in-out',
                '& fieldset': {
                    borderColor: '#e0e0e0',
                    borderWidth: '1px',
                },
                '&:hover fieldset': {
                    borderColor: '#b0b0b0',
                },
                '&.Mui-focused fieldset': {
                    borderColor: '#CC0000',
                    borderWidth: '1px !important',
                },
                '&.Mui-error fieldset': {
                    borderColor: '#CC0000 !important',
                },
            },
            input: {
                padding: '12.5px 14px',
            }
        },
    },
    MuiInputLabel: {
        styleOverrides: {
            root: {
                color: '#666666',
                backgroundColor: '#ffffff',
                padding: '0 4px',
                marginLeft: '-4px',
                '&.Mui-focused': {
                    color: '#CC0000',
                },
                '&.Mui-error': {
                    color: '#CC0000',
                },
            },
        },
    },
    MuiChip: {
        styleOverrides: {
            root: {
                borderRadius: R5_RADIUS,
                fontWeight: 700,
            },
        },
    },
    MuiIconButton: {
        styleOverrides: {
            root: {
                minWidth: 44,
                minHeight: 44,
            }
        }
    },
    MuiBottomNavigationAction: {
        styleOverrides: {
            root: {
                minWidth: 72,
                paddingTop: 8,
                paddingBottom: 8,
            },
            label: {
                fontSize: '0.75rem',
                '&.Mui-selected': {
                    fontSize: '0.75rem',
                    fontWeight: 600,
                }
            }
        }
    },
    MuiMenu: {
        styleOverrides: {
            paper: {
                borderRadius: R5_RADIUS,
            },
        },
    },
    MuiPopover: {
        styleOverrides: {
            paper: {
                borderRadius: R5_RADIUS,
            },
        },
    },
};

// Antigravity Design System — Physics & Shadows Tokens
const customDesign = {
    physics: {
        premium: 'cubic-bezier(0.19, 1, 0.22, 1)', // High-speed start, elastic finish (Cinematic)
        smooth: 'cubic-bezier(0.23, 1, 0.32, 1)',  // Fluid & elegant
    },
    shadows: {
        enterprise: '0 80px 120px rgba(0,0,0,0.15)',
        float: '0 40px 80px rgba(0,0,0,0.12)',
        glow: (color) => `0 40px 80px ${alpha(color, 0.4)}`,
    }
};

let theme = createTheme({
    palette,
    typography,
    components,
    shape: {
        borderRadius: R5_RADIUS,
    },
    design: customDesign, // Extended theme properties
    breakpoints: {
        values: {
            xs: 0,
            sm: 600,
            md: 960,
            lg: 1280,
            xl: 1920,
        },
    },
});

theme = responsiveFontSizes(theme);

export default theme;
